import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
  Timestamp
} from "firebase/firestore";
import { db } from "../firebase";
import { formatDate, formatPrice } from "../utils/format";
import {
  getReferralSettings,
  saveReferralSettings,
  getAllReferrals,
  DEFAULT_REFERRAL_SETTINGS
} from "../utils/referrals";
import {
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Tag,
  Gift,
  Copy,
  Search,
  Save,
  Users,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import toast from "react-hot-toast";

export default function AdminCouponsPage() {
  const [activeTab, setActiveTab] = useState("promos"); // "promos" | "referrals"
  const [coupons, setCoupons] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [referralSettings, setReferralSettings] = useState(DEFAULT_REFERRAL_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  // Promo search & filter
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "active" | "inactive"

  // Create coupon form
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    code: "",
    type: "percentage",
    value: "",
    minOrderAmount: "",
    maxDiscount: "",
    usageLimit: "",
    expiryDate: "",
    description: "",
    active: true,
  });

  async function loadData() {
    setLoading(true);
    try {
      const [couponsSnap, refSettings, allRefs] = await Promise.all([
        getDocs(query(collection(db, "coupons"), orderBy("createdAt", "desc"))),
        getReferralSettings(),
        getAllReferrals(),
      ]);

      setCoupons(couponsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setReferralSettings(refSettings);
      setReferrals(allRefs);
    } catch (err) {
      console.error("Error loading promo & referral data:", err);
      // Fallback query without orderBy if index is still indexing
      try {
        const couponsSnap = await getDocs(collection(db, "coupons"));
        const list = couponsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));
        setCoupons(list);
      } catch (fallbackErr) {
        console.error("Fallback load failed:", fallbackErr);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  const setFormField = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [k]: val }));
  };

  const setRefSettingField = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setReferralSettings(s => ({ ...s, [k]: val }));
  };

  // Create promo code
  async function createCoupon(e) {
    e.preventDefault();
    if (!form.code.trim() || !form.value) {
      return toast.error("Promo code and discount value are required");
    }

    const cleanCode = form.code.trim().toUpperCase();

    // Check if code already exists
    if (coupons.some(c => c.code === cleanCode)) {
      return toast.error(`A promo code "${cleanCode}" already exists!`);
    }

    try {
      let expiresAtTimestamp = null;
      if (form.expiryDate) {
        const dateObj = new Date(`${form.expiryDate}T23:59:59`);
        if (!isNaN(dateObj.getTime())) {
          expiresAtTimestamp = Timestamp.fromDate(dateObj);
        }
      }

      await addDoc(collection(db, "coupons"), {
        code: cleanCode,
        type: form.type,
        value: Number(form.value),
        minOrderAmount: form.minOrderAmount ? Number(form.minOrderAmount) : 0,
        maxDiscount: form.maxDiscount ? Number(form.maxDiscount) : null,
        usageLimit: form.usageLimit ? Number(form.usageLimit) : null,
        description: form.description ? form.description.trim() : "",
        expiresAt: expiresAtTimestamp,
        usedCount: 0,
        active: Boolean(form.active),
        createdAt: serverTimestamp(),
      });

      toast.success(`Promo code "${cleanCode}" created successfully! 🎉`);
      setShowForm(false);
      setForm({
        code: "",
        type: "percentage",
        value: "",
        minOrderAmount: "",
        maxDiscount: "",
        usageLimit: "",
        expiryDate: "",
        description: "",
        active: true,
      });
      loadData();
    } catch (err) {
      console.error("Error creating coupon:", err);
      toast.error("Failed to create promo code: " + err.message);
    }
  }

  // Toggle active/inactive with 1-click
  async function toggleActive(id, currentStatus, code) {
    const newStatus = !currentStatus;
    // Optimistic UI update
    setCoupons(prev => prev.map(c => c.id === id ? { ...c, active: newStatus } : c));

    try {
      await updateDoc(doc(db, "coupons", id), { active: newStatus });
      toast.success(`Promo code "${code}" is now ${newStatus ? "ACTIVE" : "INACTIVE"}`);
    } catch (err) {
      console.error("Error toggling coupon status:", err);
      toast.error("Could not update promo code status");
      loadData();
    }
  }

  // Delete coupon
  async function deleteCoupon(id, code) {
    if (!window.confirm(`Are you sure you want to delete promo code "${code}"?`)) return;
    try {
      await deleteDoc(doc(db, "coupons", id));
      toast.success(`Promo code "${code}" deleted`);
      setCoupons(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      toast.error("Failed to delete promo code: " + err.message);
    }
  }

  // Save referral settings
  async function handleSaveReferralSettings(e) {
    e.preventDefault();
    setSavingSettings(true);
    try {
      await saveReferralSettings({
        ...referralSettings,
        refereeDiscount: Number(referralSettings.refereeDiscount) || 10,
        referrerReward: Number(referralSettings.referrerReward) || 1000,
        minOrderAmount: Number(referralSettings.minOrderAmount) || 5000,
        maxDiscount: Number(referralSettings.maxDiscount) || 10000,
      });
      toast.success("Referral program settings saved! 🎁");
    } catch (err) {
      toast.error("Failed to save settings: " + err.message);
    } finally {
      setSavingSettings(false);
    }
  }

  // Filtered coupons
  const filteredCoupons = coupons.filter(c => {
    const matchesSearch = c.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (c.description && c.description.toLowerCase().includes(searchQuery.toLowerCase()));
    
    if (!matchesSearch) return false;
    if (statusFilter === "active") return c.active === true;
    if (statusFilter === "inactive") return c.active === false;
    return true;
  });

  // Calculate summary metrics
  const activePromoCount = coupons.filter(c => c.active).length;
  const totalRedemptions = coupons.reduce((sum, c) => sum + (c.usedCount || 0), 0);
  const totalReferralsCount = referrals.length;

  return (
    <div style={{ padding: "clamp(20px, 4vw, 44px)", maxWidth: 1280, margin: "0 auto" }}>
      
      {/* Page Title & Top Action */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28, flexWrap: "wrap", gap: 16 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <h1 style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 800, color: "var(--text)" }}>
              Promo Codes & Referrals
            </h1>
          </div>
          <p style={{ color: "var(--text-muted)", fontSize: 15 }}>
            Create discount codes, activate or deactivate promotions, and configure your customer referral links.
          </p>
        </div>

        {activeTab === "promos" && (
          <button
            onClick={() => setShowForm(s => !s)}
            className="btn btn-primary"
            style={{ padding: "12px 22px", fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}
          >
            {showForm ? "Cancel" : <><Plus size={18} /> New Promo Code</>}
          </button>
        )}
      </div>

      {/* Top Metrics Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 18, marginBottom: 28 }}>
        <div className="card" style={{ padding: "20px 22px", display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(16, 185, 129, 0.12)", color: "#10B981", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Sparkles size={22} />
          </div>
          <div>
            <p style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Active Promos</p>
            <h3 style={{ fontSize: 24, fontWeight: 800, fontFamily: "var(--font-head)", marginTop: 2 }}>
              {activePromoCount} <span style={{ fontSize: 13, color: "var(--text-muted)", fontWeight: 500 }}>of {coupons.length}</span>
            </h3>
          </div>
        </div>

        <div className="card" style={{ padding: "20px 22px", display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(59, 130, 246, 0.12)", color: "#2563EB", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <TrendingUp size={22} />
          </div>
          <div>
            <p style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Total Redemptions</p>
            <h3 style={{ fontSize: 24, fontWeight: 800, fontFamily: "var(--font-head)", marginTop: 2 }}>
              {totalRedemptions}
            </h3>
          </div>
        </div>

        <div className="card" style={{ padding: "20px 22px", display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(168, 85, 247, 0.12)", color: "#9333EA", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Users size={22} />
          </div>
          <div>
            <p style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Referred Friends</p>
            <h3 style={{ fontSize: 24, fontWeight: 800, fontFamily: "var(--font-head)", marginTop: 2 }}>
              {totalReferralsCount}
            </h3>
          </div>
        </div>

        <div className="card" style={{ padding: "20px 22px", display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: referralSettings.enabled ? "rgba(16, 185, 129, 0.12)" : "rgba(239, 68, 68, 0.12)", color: referralSettings.enabled ? "#10B981" : "#EF4444", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Gift size={22} />
          </div>
          <div>
            <p style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Referral Program</p>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: referralSettings.enabled ? "#10B981" : "#EF4444" }} />
              <span style={{ fontSize: 15, fontWeight: 700, color: referralSettings.enabled ? "#047857" : "#DC2626" }}>
                {referralSettings.enabled ? "Active" : "Disabled"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Primary Tab Switcher */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24, borderBottom: "2px solid var(--border)", paddingBottom: 10 }}>
        <button
          onClick={() => setActiveTab("promos")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 18px",
            borderRadius: 8,
            border: "none",
            cursor: "pointer",
            fontWeight: 700,
            fontSize: 14,
            transition: "all 0.2s",
            background: activeTab === "promos" ? "var(--brand)" : "transparent",
            color: activeTab === "promos" ? "#fff" : "var(--text-muted)",
          }}
        >
          <Tag size={16} /> Promo Codes & Discounts ({coupons.length})
        </button>

        <button
          onClick={() => setActiveTab("referrals")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 18px",
            borderRadius: 8,
            border: "none",
            cursor: "pointer",
            fontWeight: 700,
            fontSize: 14,
            transition: "all 0.2s",
            background: activeTab === "referrals" ? "var(--brand)" : "transparent",
            color: activeTab === "referrals" ? "#fff" : "var(--text-muted)",
          }}
        >
          <Gift size={16} /> Referral Program & Links ({referrals.length})
        </button>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          TAB 1: PROMO CODES
         ───────────────────────────────────────────────────────────── */}
      {activeTab === "promos" && (
        <>
          {/* Create Promo Code Card Drawer */}
          {showForm && (
            <div className="card" style={{ padding: 32, marginBottom: 28, background: "#fff", border: "1.5px solid var(--brand)", boxShadow: "0 10px 25px -5px rgba(0,0,0,0.08)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <div>
                  <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: 20, color: "var(--text)" }}>Create New Promo Code</h3>
                  <p style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2 }}>Configure code, discount values, redemption limits, and activation status.</p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: form.active ? "#F0FDF4" : "#F8FAFC", padding: "6px 14px", borderRadius: 20, border: `1px solid ${form.active ? "#86EFAC" : "var(--border)"}` }}>
                  <input
                    type="checkbox"
                    id="formActive"
                    checked={form.active}
                    onChange={setFormField("active")}
                    style={{ cursor: "pointer", width: 16, height: 16 }}
                  />
                  <label htmlFor="formActive" style={{ fontSize: 13, fontWeight: 700, color: form.active ? "#047857" : "var(--text-muted)", cursor: "pointer" }}>
                    {form.active ? "Activate Immediately" : "Save as Inactive"}
                  </label>
                </div>
              </div>

              <form onSubmit={createCoupon} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: 20 }}>
                <div className="form-group">
                  <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Promo Code *
                  </label>
                  <input
                    className="form-input"
                    placeholder="e.g. SOLAR20 or WELCOME5K"
                    value={form.code}
                    onChange={setFormField("code")}
                    style={{ textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.05em", fontSize: 15 }}
                    required
                  />
                </div>

                <div className="form-group">
                  <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Discount Type *
                  </label>
                  <select className="form-select" value={form.type} onChange={setFormField("type")} style={{ fontSize: 14 }}>
                    <option value="percentage">Percentage Discount (%)</option>
                    <option value="fixed">Fixed Cash Amount (₦)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Discount Value * {form.type === "percentage" ? "(%)" : "(₦)"}
                  </label>
                  <input
                    className="form-input"
                    type="number"
                    min={1}
                    max={form.type === "percentage" ? 100 : undefined}
                    placeholder={form.type === "percentage" ? "15" : "5000"}
                    value={form.value}
                    onChange={setFormField("value")}
                    style={{ fontWeight: 600, fontSize: 15 }}
                    required
                  />
                </div>

                <div className="form-group">
                  <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Minimum Order Amount (₦)
                  </label>
                  <input
                    className="form-input"
                    type="number"
                    min={0}
                    placeholder="e.g. 20000 (0 for no min)"
                    value={form.minOrderAmount}
                    onChange={setFormField("minOrderAmount")}
                  />
                </div>

                <div className="form-group">
                  <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Maximum Discount Cap (₦) — % only
                  </label>
                  <input
                    className="form-input"
                    type="number"
                    min={0}
                    placeholder="e.g. 10000"
                    disabled={form.type !== "percentage"}
                    value={form.maxDiscount}
                    onChange={setFormField("maxDiscount")}
                  />
                </div>

                <div className="form-group">
                  <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Usage Limit (Max Uses)
                  </label>
                  <input
                    className="form-input"
                    type="number"
                    min={1}
                    placeholder="Leave blank for unlimited"
                    value={form.usageLimit}
                    onChange={setFormField("usageLimit")}
                  />
                </div>

                <div className="form-group">
                  <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Expiry Date
                  </label>
                  <input
                    className="form-input"
                    type="date"
                    value={form.expiryDate}
                    onChange={setFormField("expiryDate")}
                  />
                </div>

                <div className="form-group">
                  <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Description / Internal Note
                  </label>
                  <input
                    className="form-input"
                    placeholder="e.g. Summer promotion for battery buyers"
                    value={form.description}
                    onChange={setFormField("description")}
                  />
                </div>

                <div style={{ gridColumn: "1 / -1", display: "flex", gap: 12, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
                  <button type="submit" className="btn btn-primary" style={{ padding: "12px 28px" }}>
                    Save & Publish Promo Code
                  </button>
                  <button type="button" onClick={() => setShowForm(false)} className="btn btn-secondary" style={{ padding: "12px 20px" }}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Search & Filter Toolbar */}
          <div className="card" style={{ padding: "16px 20px", marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <div style={{ position: "relative", flex: 1, minWidth: 240, maxWidth: 400 }}>
              <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
              <input
                className="form-input"
                placeholder="Search promo codes or notes…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{ paddingLeft: 36, fontSize: 13, height: 40 }}
              />
            </div>

            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-muted)", marginRight: 4, textTransform: "uppercase" }}>Status:</span>
              {[
                { id: "all", label: "All" },
                { id: "active", label: "Active Only" },
                { id: "inactive", label: "Inactive" },
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setStatusFilter(f.id)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 6,
                    border: "1px solid",
                    borderColor: statusFilter === f.id ? "var(--brand)" : "var(--border)",
                    background: statusFilter === f.id ? "var(--brand-light)" : "#fff",
                    color: statusFilter === f.id ? "var(--brand)" : "var(--text-muted)",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Promo Codes Table */}
          <div className="card" style={{ overflow: "hidden", background: "#fff", border: "1px solid var(--border)", padding: 0 }}>
            {loading ? (
              <div style={{ padding: 60, textAlign: "center" }}><div className="loader"><div className="spinner" /></div></div>
            ) : filteredCoupons.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <Tag size={44} color="var(--text-muted)" style={{ marginBottom: 12 }} />
                <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 700, marginBottom: 6 }}>No promo codes found</h3>
                <p style={{ color: "var(--text-muted)", fontSize: 14, maxWidth: 360, margin: "0 auto 20px" }}>
                  {searchQuery || statusFilter !== "all"
                    ? "Try clearing your search query or status filter."
                    : "Create your first discount promo code to offer instant customer savings."}
                </p>
                <button onClick={() => setShowForm(true)} className="btn btn-primary btn-sm">
                  <Plus size={14} /> Create Promo Code
                </button>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                  <thead>
                    <tr style={{ background: "var(--surface)", borderBottom: "2px solid var(--border)" }}>
                      {["Promo Code", "Discount", "Conditions", "Redemptions", "Expiry", "Active Status", ""].map(h => (
                        <th key={h} style={{ padding: "14px 18px", textAlign: "left", fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCoupons.map(c => {
                      let isExpired = false;
                      if (c.expiresAt) {
                        const expiryDate = c.expiresAt.toDate ? c.expiresAt.toDate() : new Date(c.expiresAt);
                        if (expiryDate && !isNaN(expiryDate.getTime()) && expiryDate < new Date()) {
                          isExpired = true;
                        }
                      }

                      return (
                        <tr
                          key={c.id}
                          style={{
                            borderBottom: "1px solid var(--border)",
                            transition: "background 0.15s",
                            background: !c.active ? "rgba(0,0,0,0.015)" : "transparent",
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = "#F8FAFC"}
                          onMouseLeave={e => e.currentTarget.style.background = !c.active ? "rgba(0,0,0,0.015)" : "transparent"}
                        >
                          {/* Code */}
                          <td style={{ padding: "16px 18px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontFamily: "monospace", fontWeight: 800, fontSize: 15, color: "var(--brand)", background: "rgba(30, 58, 138, 0.08)", padding: "4px 8px", borderRadius: 6 }}>
                                {c.code}
                              </span>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(c.code);
                                  toast.success(`Copied code "${c.code}"`);
                                }}
                                style={{ border: "none", background: "transparent", color: "var(--text-muted)", cursor: "pointer", padding: 2 }}
                                title="Copy code"
                              >
                                <Copy size={13} />
                              </button>
                            </div>
                            {c.description && (
                              <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4, maxWidth: 220 }}>
                                {c.description}
                              </p>
                            )}
                          </td>

                          {/* Discount */}
                          <td style={{ padding: "16px 18px" }}>
                            <div style={{ fontWeight: 800, fontSize: 15, color: "var(--text)" }}>
                              {c.type === "percentage" ? `${c.value}% OFF` : `${formatPrice(c.value)} OFF`}
                            </div>
                            <span style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "capitalize" }}>
                              {c.type} discount
                            </span>
                          </td>

                          {/* Conditions */}
                          <td style={{ padding: "16px 18px", fontSize: 13, color: "var(--text-muted)" }}>
                            <div>Min: {c.minOrderAmount ? formatPrice(c.minOrderAmount) : "None"}</div>
                            {c.type === "percentage" && c.maxDiscount ? (
                              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>Max Cap: {formatPrice(c.maxDiscount)}</div>
                            ) : null}
                          </td>

                          {/* Redemptions vs Limit */}
                          <td style={{ padding: "16px 18px" }}>
                            <div style={{ fontWeight: 700, fontSize: 14 }}>
                              {c.usedCount || 0}
                              <span style={{ fontWeight: 500, color: "var(--text-muted)", fontSize: 12 }}>
                                {" "}/ {c.usageLimit ? c.usageLimit : "∞"}
                              </span>
                            </div>
                            {c.usageLimit && (
                              <div style={{ width: 80, height: 4, background: "#E2E8F0", borderRadius: 2, marginTop: 4, overflow: "hidden" }}>
                                <div
                                  style={{
                                    width: `${Math.min(100, Math.round(((c.usedCount || 0) / c.usageLimit) * 100))}%`,
                                    height: "100%",
                                    background: (c.usedCount || 0) >= c.usageLimit ? "#EF4444" : "var(--brand)",
                                  }}
                                />
                              </div>
                            )}
                          </td>

                          {/* Expiry */}
                          <td style={{ padding: "16px 18px", fontSize: 13 }}>
                            {c.expiresAt ? (
                              <div>
                                <span>{formatDate(c.expiresAt)}</span>
                                {isExpired && (
                                  <div style={{ display: "inline-block", marginLeft: 4, fontSize: 10, background: "#FEE2E2", color: "#DC2626", padding: "1px 6px", borderRadius: 4, fontWeight: 700 }}>
                                    EXPIRED
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span style={{ color: "var(--text-muted)" }}>Never</span>
                            )}
                          </td>

                          {/* Active Status & 1-Click Toggle */}
                          <td style={{ padding: "16px 18px" }}>
                            <button
                              onClick={() => toggleActive(c.id, c.active, c.code)}
                              style={{
                                border: "1px solid",
                                borderColor: c.active ? "#86EFAC" : "#CBD5E1",
                                background: c.active ? "#F0FDF4" : "#F8FAFC",
                                borderRadius: 20,
                                padding: "6px 12px",
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                color: c.active ? "#047857" : "#64748B",
                                transition: "all 0.2s",
                              }}
                              title={c.active ? "Click to deactivate" : "Click to activate"}
                            >
                              {c.active ? <ToggleRight size={20} color="#10B981" /> : <ToggleLeft size={20} color="#94A3B8" />}
                              <span style={{ fontSize: 12, fontWeight: 700 }}>
                                {c.active ? "Active" : "Inactive"}
                              </span>
                            </button>
                          </td>

                          {/* Delete */}
                          <td style={{ padding: "16px 18px", textAlign: "right" }}>
                            <button
                              onClick={() => deleteCoupon(c.id, c.code)}
                              style={{
                                border: "none",
                                color: "#EF4444",
                                padding: 8,
                                borderRadius: 6,
                                background: "rgba(239, 68, 68, 0.08)",
                                display: "inline-flex",
                                alignItems: "center",
                                cursor: "pointer",
                                transition: "background 0.2s",
                              }}
                              title="Delete Promo Code"
                            >
                              <Trash2 size={15} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 2: REFERRAL PROGRAM & LOGS
         ───────────────────────────────────────────────────────────── */}
      {activeTab === "referrals" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          
          {/* Referral Program Configuration Card */}
          <div className="card" style={{ padding: 32, background: "#fff", border: "1px solid var(--border)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
              <div>
                <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 800, fontSize: 20, color: "var(--text)" }}>
                  Referral Program Settings
                </h3>
                <p style={{ fontSize: 14, color: "var(--text-muted)", marginTop: 2 }}>
                  Control whether referral links are enabled and configure the discount referees receive when joining.
                </p>
              </div>

              {/* Master toggle */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, background: referralSettings.enabled ? "#F0FDF4" : "#F8FAFC", padding: "8px 16px", borderRadius: 24, border: `1px solid ${referralSettings.enabled ? "#86EFAC" : "var(--border)"}` }}>
                <input
                  type="checkbox"
                  id="refEnabled"
                  checked={referralSettings.enabled}
                  onChange={setRefSettingField("enabled")}
                  style={{ cursor: "pointer", width: 18, height: 18 }}
                />
                <label htmlFor="refEnabled" style={{ fontSize: 14, fontWeight: 700, color: referralSettings.enabled ? "#047857" : "var(--text-muted)", cursor: "pointer" }}>
                  {referralSettings.enabled ? "Program Active" : "Program Disabled"}
                </label>
              </div>
            </div>

            <form onSubmit={handleSaveReferralSettings} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: 20 }}>
              <div className="form-group">
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Referee Discount Type
                </label>
                <select
                  className="form-select"
                  value={referralSettings.discountType}
                  onChange={setRefSettingField("discountType")}
                >
                  <option value="percentage">Percentage Discount (%)</option>
                  <option value="fixed">Fixed Cash Discount (₦)</option>
                </select>
              </div>

              <div className="form-group">
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  New User Welcome Discount {referralSettings.discountType === "percentage" ? "(%)" : "(₦)"}
                </label>
                <input
                  className="form-input"
                  type="number"
                  min={1}
                  value={referralSettings.refereeDiscount}
                  onChange={setRefSettingField("refereeDiscount")}
                  required
                />
              </div>

              <div className="form-group">
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Referrer Reward / Bonus (₦)
                </label>
                <input
                  className="form-input"
                  type="number"
                  min={0}
                  value={referralSettings.referrerReward}
                  onChange={setRefSettingField("referrerReward")}
                />
              </div>

              <div className="form-group">
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Min Order Amount to Qualify (₦)
                </label>
                <input
                  className="form-input"
                  type="number"
                  min={0}
                  value={referralSettings.minOrderAmount}
                  onChange={setRefSettingField("minOrderAmount")}
                />
              </div>

              <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Program Promotion Description
                </label>
                <input
                  className="form-input"
                  value={referralSettings.programDescription}
                  onChange={setRefSettingField("programDescription")}
                  placeholder="Shown to users on their referral dashboard..."
                />
              </div>

              <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end", borderTop: "1px solid var(--border)", paddingTop: 16 }}>
                <button type="submit" disabled={savingSettings} className="btn btn-primary" style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 24px" }}>
                  <Save size={16} /> {savingSettings ? "Saving…" : "Save Referral Settings"}
                </button>
              </div>
            </form>
          </div>

          {/* All Platform Referrals History Table */}
          <div className="card" style={{ padding: 0, overflow: "hidden", border: "1px solid var(--border)", background: "#fff" }}>
            <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h3 style={{ fontFamily: "var(--font-head)", fontSize: 18, fontWeight: 700 }}>
                  Referral Activity Log ({referrals.length})
                </h3>
                <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
                  Audit trail of all signups generated through user referral links.
                </p>
              </div>
            </div>

            {loading ? (
              <div style={{ padding: 60, textAlign: "center" }}><div className="loader"><div className="spinner" /></div></div>
            ) : referrals.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 20px" }}>
                <Gift size={44} color="var(--text-muted)" style={{ marginBottom: 12 }} />
                <h3 style={{ fontFamily: "var(--font-head)", fontWeight: 700, marginBottom: 6 }}>No referrals yet</h3>
                <p style={{ color: "var(--text-muted)", fontSize: 14, maxWidth: 360, margin: "0 auto" }}>
                  When customers share their referral links, signups will appear here in real-time.
                </p>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                  <thead>
                    <tr style={{ background: "var(--surface)", borderBottom: "2px solid var(--border)" }}>
                      {["Referrer (Sender)", "Referral Code", "Referee (Friend)", "Date Joined", "Status"].map(h => (
                        <th key={h} style={{ padding: "14px 18px", textAlign: "left", fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.map(r => (
                      <tr key={r.id} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: "16px 18px" }}>
                          <div style={{ fontWeight: 700 }}>{r.referrerName || "User"}</div>
                          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{r.referrerEmail}</div>
                        </td>
                        <td style={{ padding: "16px 18px" }}>
                          <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 13, background: "rgba(30, 58, 138, 0.08)", padding: "2px 8px", borderRadius: 4, color: "var(--brand)" }}>
                            {r.referralCode}
                          </span>
                        </td>
                        <td style={{ padding: "16px 18px" }}>
                          <div style={{ fontWeight: 600 }}>{r.refereeName || "Customer"}</div>
                          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{r.refereeEmail}</div>
                        </td>
                        <td style={{ padding: "16px 18px", color: "var(--text-muted)", fontSize: 13 }}>
                          {formatDate(r.createdAt)}
                        </td>
                        <td style={{ padding: "16px 18px" }}>
                          <span
                            className="badge"
                            style={{
                              background: r.status === "completed_purchase" ? "rgba(16, 185, 129, 0.12)" : "rgba(59, 130, 246, 0.12)",
                              color: r.status === "completed_purchase" ? "#047857" : "#1D4ED8",
                              fontWeight: 700,
                              padding: "4px 8px",
                              borderRadius: 6,
                              fontSize: 12,
                            }}
                          >
                            {r.status === "completed_purchase" ? "Purchased 🎉" : "Registered"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      )}

    </div>
  );
}
